"use client";

import clsx from "clsx";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import Button, {ButtonProps} from "@/components/library/Button";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from "@/components/ui/dialog";
import {IconName} from "@fortawesome/fontawesome-common-types";

export interface ModalHeaderProps {
   /** Modal title - required */
   title: string | React.ReactNode;
   /** Optional description below title */
   description?: string | React.ReactNode;
   /** Optional icon displayed before title */
   icon?: IconName;
   /** Optional className for icon styling (e.g., custom colors) */
   iconClassName?: string;
   /** Optional extra content in header (e.g., links, buttons) */
   extra?: React.ReactNode;
   /** Optional className for header styling */
   className?: string;
}

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

export interface ModalProps {
   /** Whether the modal is open */
   open: boolean;
   /** Callback when open state changes */
   onOpenChange: (open: boolean) => void;

   /** Whether to use modal behavior (default: true) */
   modal?: boolean;
   /** Whether to blur the backdrop */
   blurBackdrop?: boolean;

   /** Header configuration */
   header: ModalHeaderProps;

   /** Modal body content (always scrollable) */
   children: React.ReactNode;

   /**
    * Array of buttons for footer
    * Buttons are rendered in order from left to right
    * Convention: Primary/action buttons first, Cancel/Close button last (far right)
    * Uses ButtonProps from library Button component
    */
   buttons?: ButtonProps[];

   /** Custom footer content rendered alongside buttons (on the left) */
   footer?: React.ReactNode;

   /** Additional className for DialogContent */
   className?: string;

   /**
    * Size preset for modal width
    * - sm: max-w-md (448px) - Confirmations, simple forms
    * - md: max-w-lg (512px) - Default, standard dialogs
    * - lg: max-w-2xl (672px) - Forms with more fields
    * - xl: max-w-4xl (896px) - Complex content, tables
    * - full: max-w-6xl (1152px) - Full-featured modals
    */
   size?: ModalSize;

   /**
    * Allow content to overflow (for dropdowns/popovers)
    * When true, removes overflow-hidden from the dialog
    */
   allowOverflow?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
   sm: "max-w-md",
   md: "max-w-lg",
   lg: "max-w-2xl",
   xl: "max-w-4xl",
   full: "max-w-6xl",
};

/**
 * Modal component - An opinionated wrapper around Dialog with consistent
 * header and footer patterns.
 *
 * Features:
 * - Consistent header with optional icon, title, description, and extra content
 * - Always scrollable body content
 * - Footer with buttons array and/or custom content
 * - Size presets for common use cases
 *
 * Button order convention: Primary/action buttons first, Cancel/Close last (far right)
 *
 * @example
 * // Simple confirmation modal
 * <Modal
 *   open={open}
 *   onOpenChange={setOpen}
 *   header={{
 *     title: "Delete Item",
 *     description: "Are you sure you want to delete this item?",
 *   }}
 *   size="sm"
 *   buttons={[
 *     { type: "primary", destructive: true, onClick: handleDelete, children: "Delete" },
 *     { type: "secondary", onClick: () => setOpen(false), children: "Cancel" },
 *   ]}
 * >
 *   <p>This action cannot be undone.</p>
 * </Modal>
 */
export default function Modal({
   open,
   onOpenChange,
   modal = true,
   blurBackdrop,
   header,
   children,
   buttons,
   footer,
   className,
   size = "md",
   allowOverflow = false,
}: Readonly<ModalProps>) {
   const hasFooter = (buttons && buttons.length > 0) || footer;
   const scrollRef = useRef<HTMLDivElement>(null);
   const [canScrollDown, setCanScrollDown] = useState(false);

   const checkScroll = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      const threshold = 8;
      setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > threshold);
   }, []);

   useEffect(() => {
      if (!open) return;
      const frame = requestAnimationFrame(checkScroll);
      return () => cancelAnimationFrame(frame);
   }, [open, checkScroll]);

   return (
      <Dialog open={open} onOpenChange={onOpenChange} modal={modal} blurBackdrop={blurBackdrop}>
         <DialogContent
            className={clsx(
               sizeClasses[size],
               // Always have margin around the dialog (never full-full width)
               "!w-[calc(100%-2rem)] sm:!w-[calc(100%-4rem)]",
               // Max height with proper flex layout for scrolling
               "max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]",
               // Override base grid display with flex for proper scrolling
               "!flex !flex-col",
               // Allow overflow for dropdowns/popovers when needed
               allowOverflow ? "overflow-visible" : "overflow-hidden",
               className
            )}
         >
            {/* Header - flex-shrink-0 to prevent shrinking */}
            <DialogHeader className={clsx("flex-shrink-0 pr-8", header.className)}>
               <DialogTitle className="flex items-center gap-2">
                  {header.icon && (
                     <Icon
                        icon={header.icon}
                        size={ICON_SIZES.LG}
                        className={header.iconClassName}
                     />
                  )}
                  <span>{header.title}</span>
               </DialogTitle>
               {header.description && <DialogDescription>{header.description}</DialogDescription>}
               {header.extra}
            </DialogHeader>

            {/* Scrollable Body - min-h-0 is critical for flex children to scroll */}
            <div
               ref={scrollRef}
               onScroll={checkScroll}
               className={clsx(
                  "flex-1 min-h-0",
                  allowOverflow ? "overflow-visible" : "overflow-y-auto"
               )}
               data-dialog-scroll
            >
               <div className="py-2 px-1">{children}</div>
               {canScrollDown && (
                  <div
                     className="pointer-events-none sticky bottom-0 h-8 -mt-8 bg-gradient-to-t from-panel dark:from-gray-800 to-transparent flex items-end justify-center pb-1"
                     aria-hidden
                  >
                     <Icon
                        icon="chevron-down"
                        size={ICON_SIZES.SM}
                        className="text-muted-foreground animate-bounce"
                     />
                  </div>
               )}
            </div>

            {/* Footer - flex-shrink-0 to prevent shrinking */}
            {hasFooter && (
               <DialogFooter className="flex-shrink-0 pt-4">
                  <div className="flex justify-between items-center w-full">
                     {/* Custom footer content on left */}
                     <div className="flex-1">{footer}</div>

                     {/* Buttons on right */}
                     {buttons && buttons.length > 0 && (
                        <div className="flex items-center gap-2">
                           {buttons.map((buttonProps, idx) => (
                              <Button
                                 key={buttonProps.id ?? buttonProps.testId ?? `modal-btn-${idx}`}
                                 {...buttonProps}
                              />
                           ))}
                        </div>
                     )}
                  </div>
               </DialogFooter>
            )}
         </DialogContent>
      </Dialog>
   );
}

// Re-export types for convenience
export type {ButtonProps} from "@/components/library/Button";
